/**
 * Checkbox — Binary toggle for opt-in form choices.
 *
 * Checkbox Component Guidelines
 * - Use Checkbox for choices submitted as part of a form (e.g. "I agree to terms").
 *   For instant-effect toggles (e.g. settings, feature flags), prefer Switch instead.
 * - Always pair with `<Field>` + `<FieldLabel>` for accessibility — a standalone
 *   Checkbox has no visible or programmatic label.
 * - Set `invalid` when validation fails to show error styling. Pair with a sibling
 *   `<FieldError>` to display the message.
 * - Use inside `<FieldSet>` + `<FieldLegend>` when presenting a group of checkboxes
 *   as a multi-select option set.
 * @see https://base-ui.com/react/components/checkbox Base UI Checkbox documentation
 * @see https://ui.shadcn.com/docs/components/checkbox shadcn/ui Checkbox documentation
 */
import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";

import React from "react";

import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";
import twMerge from "@replo/design-system/utils/twMerge";
import { CheckIcon } from "lucide-react";

interface CheckboxProps<ClassName extends string = string>
  extends Omit<CheckboxPrimitive.Root.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  invalid?: boolean;
}

function CheckboxInner<ClassName extends string>(
  {
    className,
    UNSAFE_className,
    invalid,
    onClick,
    ...props
  }: CheckboxProps<ClassName>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      data-slot="checkbox"
      aria-invalid={invalid}
      className={twMerge(
        "outline-none border-component-border data-checked:bg-default data-checked:text-white data-checked:border-default aria-invalid:border-rose-border data-checked:aria-invalid:border-rose-border aria-invalid:ring-3 aria-invalid:ring-rose-border focus-visible:border-ring focus-visible:ring-ring/50 flex size-4 items-center justify-center rounded-[4px] border transition-colors group-has-disabled/field:opacity-50 focus-visible:ring-0.5 peer relative shrink-0 after:absolute after:-inset-x-3 after:-inset-y-2 cursor-pointer data-disabled:cursor-not-allowed data-disabled:opacity-50",
        className,
        UNSAFE_className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="[&>svg]:size-3.5 grid place-content-center text-current transition-none"
      >
        <CheckIcon />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

// NOTE (Ben, 01/22/2026): Type cast to preserve generic type parameter for className prop
// See: https://fettblog.eu/typescript-react-generic-forward-refs/
const Checkbox = React.forwardRef(CheckboxInner) as <ClassName extends string>(
  props: CheckboxProps<ClassName> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode;

export { Checkbox };
